import { action, computed, observable } from 'mobx';
import { sleep } from '@linktivity/link-utils';
import {
  ACCESS_TOKEN,
  MAX_REFRESH_COUNT,
  TIMEOUT,
  ROLE
} from '@link/share/constants';
import { Claims } from '@link/share/services/types';
import { User, UserCredential } from '@link/share/vendors/firebase';

const storage = localStorage;

export default class {
  @observable accessor user: User | null = null;
  @observable accessor claims: Claims | null = null;
  @observable accessor accessToken = storage.getItem(ACCESS_TOKEN) || '';
  @observable accessor loading: boolean = true;

  @action
  setUser(user: User) {
    this.user = user;
    this.loading = true;
    this.getClaims(user);
  }

  @action
  async getClaims(user: User) {
    try {
      const tokenResult = await user.getIdTokenResult();
      if (tokenResult) {
        this.setClaims(tokenResult.claims as unknown as Claims);
      }
    } finally {
      this.setLoading(false);
    }
  }
  @action
  setClaims(claims: Claims) {
    this.claims = claims;
  }
  @action
  clearUser() {
    this.user = null;
    try {
      storage.removeItem(ACCESS_TOKEN);
    } finally {
      this.accessToken = '';
    }
  }

  @action
  setToken(token: string) {
    try {
      storage.setItem(ACCESS_TOKEN, token);
    } finally {
      this.accessToken = token;
    }
  }

  @action
  async getToken() {
    if (this.user) {
      const token = await this.user.getIdToken();
      this.setToken(token);
      return token;
    } else {
      return '';
    }
  }

  @action
  getRefreshedToken = async () => {
    let count = 0;
    const requestToken: () => Promise<string> = async () => {
      const token = await this.getToken();
      if (token) {
        return token;
      } else {
        if (count < MAX_REFRESH_COUNT) {
          count++;
          await sleep(TIMEOUT);
          return await requestToken();
        } else {
          return '';
        }
      }
    };
    return await requestToken();
  };

  @action
  updateToken(credential: UserCredential) {
    credential.user.getIdToken().then(token => {
      this.setToken(token);
    });
  }

  @computed
  get isPasswordLogin(): boolean {
    return this.user?.providerData[0]?.providerId === 'password';
  }

  @computed
  get loggedIn(): boolean {
    return !!this.user || !!this.accessToken;
  }

  @action
  setLoading(loading: boolean) {
    this.loading = loading;
  }

  @computed
  get isLinkAdmin(): boolean {
    // 账号角色
    // enum Role {
    // UNDEFINED_ROLE = 0; // 未定义
    // ADMIN = 1; // 管理员角色
    // VIEWER = 2; // 查看角色
    // EDITOR = 3; // 编辑角色
    // OPERATOR = 4; // 操作员角色
    // LINK_ADMIN = 5; // Linktivity管理员角色
    // SUPERVISOR = 6; // 监督角色
    // }
    return (
      this.claims?.ADMIN_SERVICE?.role === ROLE.LINK_ADMIN ||
      this.claims?.ADMIN_SERVICE?.role === ROLE.SUPERVISOR
    );
  }

  @computed
  get isMfaRequired(): boolean {
    return !!this.claims?.mfa_required;
  }
}
